/**
 * Renders remote players with the third-person PlayerModel and a vanilla-ish
 * nametag (white text on a translucent black plate, visible through walls).
 * Fed each frame with interpolated views from NetSession.remotePlayers().
 */

import * as THREE from 'three';
import type { PlayerPoseView } from '../game/types';
import type { EnvUniforms } from '../render/env';
import { PlayerModel } from '../render/entities/playerModel';
import { PF_BOW, PF_DEAD, PF_FIRE, PF_FLY, PF_SNEAK, PF_SPRINT, PF_SWIM } from './codec';
import type { RemotePlayerView } from './session';

interface Shown { model: PlayerModel; tag: THREE.Sprite; name: string; pose: PlayerPoseView }

function nameTag(name: string): THREE.Sprite {
  const c = document.createElement('canvas');
  const ctx = c.getContext('2d')!;
  const font = '28px monospace';
  ctx.font = font;
  const w = Math.ceil(ctx.measureText(name).width) + 12;
  c.width = w; c.height = 36;
  ctx.font = font;
  ctx.fillStyle = 'rgba(0,0,0,0.25)';
  ctx.fillRect(0, 0, w, 36);
  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, 6, 19);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true });
  const s = new THREE.Sprite(mat);
  s.scale.set(w / 36 * 0.25, 0.25, 1);
  s.renderOrder = 10;
  return s;
}

export class RemotePlayers {
  private readonly shown = new Map<string, Shown>();
  private readonly views: RemotePlayerView[] = [];

  constructor(
    private readonly scene: THREE.Scene,
    private readonly env: EnvUniforms,
    private readonly light: (x: number, y: number, z: number) => number,
  ) {}

  update(source: { remotePlayers(out: RemotePlayerView[]): RemotePlayerView[] } | null): void {
    const views = source ? source.remotePlayers(this.views) : [];
    const live = new Set<string>();
    for (const r of views) {
      live.add(r.id);
      let s = this.shown.get(r.id);
      if (!s || s.name !== r.name) {
        if (s) this.remove(r.id);
        const model = new PlayerModel(this.env);
        const tag = nameTag(r.name);
        this.scene.add(model.group, tag);
        s = { model, tag, name: r.name, pose: blankPose() };
        this.shown.set(r.id, s);
      }
      const v = r.v, st = r.state, p = s.pose;
      p.x = p.px = v[0]; p.y = p.py = v[1]; p.z = p.pz = v[2];
      p.headYaw = v[4]; p.pitch = v[5];
      p.yaw = v[3];
      p.limbSwing = v[6]; p.limbSwingAmount = v[7]; p.swing = v[8];
      p.sneaking = (st.flags & PF_SNEAK) !== 0;
      p.sprinting = (st.flags & PF_SPRINT) !== 0;
      p.swimming = (st.flags & PF_SWIM) !== 0;
      p.flying = (st.flags & PF_FLY) !== 0;
      p.usingBow = (st.flags & PF_BOW) !== 0;
      p.onFire = (st.flags & PF_FIRE) !== 0;
      p.hurtTime = st.hurtTime;
      p.heldItem = st.heldItem;
      p.armor = st.armor;
      p.light = this.light(v[0], v[1] + 1, v[2]);
      const dead = (st.flags & PF_DEAD) !== 0;
      s.model.setVisible(!dead);
      s.model.update(p, 1);
      s.tag.visible = !dead;
      s.tag.position.set(v[0], v[1] + (p.sneaking ? 1.85 : 2.1), v[2]);
    }
    for (const id of [...this.shown.keys()]) if (!live.has(id)) this.remove(id);
  }

  private remove(id: string): void {
    const s = this.shown.get(id);
    if (!s) return;
    s.model.group.removeFromParent();
    s.tag.removeFromParent();
    s.model.dispose();
    (s.tag.material as THREE.SpriteMaterial).map?.dispose();
    s.tag.material.dispose();
    this.shown.delete(id);
  }

  clear(): void { for (const id of [...this.shown.keys()]) this.remove(id); }
}

function blankPose(): PlayerPoseView {
  return {
    x: 0, y: 0, z: 0, px: 0, py: 0, pz: 0, yaw: 0, headYaw: 0, pitch: 0, limbSwing: 0, limbSwingAmount: 0,
    swing: 0, sneaking: false, sprinting: false, swimming: false, flying: false, usingBow: false, hurtTime: 0,
    heldItem: 0, armor: [0, 0, 0, 0], onFire: false, light: 1,
  };
}
